import { Capacitor } from "@capacitor/core";
import { AppReview } from "@capawesome/capacitor-app-review";

/** Numeric Apple App Store ID (from apps.apple.com/app/idXXXXXXXX). Required for iOS store fallback. */
const IOS_APP_STORE_ID = process.env.REACT_APP_IOS_APP_STORE_ID?.trim() || "";

const nativePlatform = (): "ios" | "android" | null => {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") return platform;
  return null;
};

export const isNativeAppReviewSupported = (): boolean => nativePlatform() !== null;

/** @deprecated Use isNativeAppReviewSupported */
export const isIosNativeAppReviewSupported = (): boolean =>
  nativePlatform() === "ios";

/**
 * User-initiated review (Help menu). Native in-app prompt, then store fallback.
 * Reviews live in App Store Connect / Google Play Console — not in our backend.
 */
export async function promptNativeAppReview(): Promise<void> {
  const platform = nativePlatform();
  if (!platform) {
    return;
  }

  try {
    await AppReview.requestReview();
  } catch (error) {
    console.warn("[appReview] requestReview failed, trying store fallback:", error);
    if (platform === "ios") {
      await openIosAppStoreReviewPage();
    } else {
      await openAndroidPlayStoreReviewPage();
    }
  }
}

/** @deprecated Use promptNativeAppReview */
export const promptIosAppReview = promptNativeAppReview;

async function openIosAppStoreReviewPage(): Promise<void> {
  if (!IOS_APP_STORE_ID) {
    console.warn(
      "[appReview] Set REACT_APP_IOS_APP_STORE_ID in .env for App Store fallback (numeric id from App Store Connect)."
    );
    return;
  }

  try {
    await AppReview.openAppStore({ appId: IOS_APP_STORE_ID });
  } catch (error) {
    console.error("[appReview] openAppStore (iOS) failed:", error);
  }
}

async function openAndroidPlayStoreReviewPage(): Promise<void> {
  try {
    // Uses the app package from the native Android project (com.rotogpt.chat).
    await AppReview.openAppStore();
  } catch (error) {
    console.error("[appReview] openAppStore (Android) failed:", error);
  }
}
